import { createContext, useContext } from 'react';
import { useSelector } from 'react-redux';
import core, { App, GQL, Poller } from '@gin-betting/core';
import type matchList from '@gin-betting/match-list';
import { noop, omit } from 'lodash';
import { createStructuredSelector } from 'reselect';

import { MatchListType } from 'app-constants';
import { useAsyncModule } from 'async-modules';
import { useDidUpdate, useMount, useWithLocation } from 'hooks';
import ui from 'modules/ui';
import type { State } from 'types';
import {
    checkIfNewsPage,
    getVariablesForLiveSportEvent,
    parse,
    stringify,
} from 'utils';

type MatchListModule = typeof matchList;

const {
    poller: { usePoller, getPollerManager },
    utils: {
        getBettingOptions,
        getDefaultVariables,
        getAppliedFilters,
        getSportTypeFromLocation,
    },
    viewport: {
        selectors: { selectIsCompact },
    },
} = core;

const {
    selectors: { selectIsFilterOpened },
} = ui;

interface Input {
    pollerOpts: Poller.UsePollerInput<
        MatchListModule['actions']['searchMatches']
    >;
    type: MatchListType;
}

interface Output {
    shouldRenderMatchList: boolean;
}

const FILTER_LOCATION_SEARCH: Array<keyof App.SearchObj> = ['page'];

const ROUTES_WITH_LIST = [MatchListType.LIVE, MatchListType.PREMATCH];

const DEFAULT_CONTEXT = createContext({
    currentOverlay: 'DEFAULT_CONTEXT' as any,
    setOverlay: noop,
});

const useMatchListContainer = ({ pollerOpts, type }: Input): Output => {
    const filter = useAsyncModule('filters');
    const { currentOverlay } = useContext(
        filter?.MobileFiltersCtx || DEFAULT_CONTEXT
    );
    const { isCompact, isCategorizerOpened } = useSelector((s: State) =>
        selectProps(s, {})
    );

    const isRouteWithList = ROUTES_WITH_LIST.includes(type);

    const { search, sportType, variables } = useWithLocation(
        ({ search: s }) => ({
            search: stringify(omit(parse(s), FILTER_LOCATION_SEARCH)),
            sportType: getSportTypeFromLocation(),
            variables: getPollerVariables(type),
        })
    );

    const { requestContent } = usePoller({
        ...pollerOpts,
        skipInitialRequest: true, // NOTE: prevent request content in usePoller. Request content placed in useMount
        variables,
    });

    useMount(() => {
        const { pollerName, skipInitialRequest } = pollerOpts;
        const poller = getPollerManager().get(pollerName);

        if (!poller) return;

        poller.enable({ skipRequest: skipInitialRequest });

        return () => {
            poller.disable();
        };
    });

    useDidUpdate(() => {
        requestContent();
    }, [requestContent, sportType, search]);

    return {
        shouldRenderMatchList:
            !isCompact ||
            ((isRouteWithList || !isCategorizerOpened) &&
                currentOverlay !==
                    filter?.constants.OverlayType.FEATURED_TOURNAMENTS_LIST),
    };
};

const selectProps = createStructuredSelector({
    isCompact: selectIsCompact,
    isCategorizerOpened: selectIsFilterOpened,
});

function getPollerVariables(
    type: MatchListType
): Partial<GQL.GetMatchesByFiltersQueryVariables> {
    const { sportId, tournamentId } = getAppliedFilters();
    const { selectedSportIds } = getBettingOptions();

    const isNewsPage = checkIfNewsPage();

    const {
        marketStatuses,
        matchStatuses,
        ...restVariables
    } = getDefaultVariables();

    const {
        marketStatusesForLiveSportEvent,
        dateFrom,
    } = getVariablesForLiveSportEvent(matchStatuses, marketStatuses);

    return {
        dateFrom,
        matchStatuses,
        marketStatuses,
        ...restVariables,
        ...(tournamentId && { tournamentIds: [tournamentId] }),
        ...(sportId && { sportIds: [sportId] }),
        ...(isNewsPage &&
            selectedSportIds.length && { sportIds: selectedSportIds }),
        marketStatusesForSportEvent: marketStatusesForLiveSportEvent,
        marketLimit: 3,
        isTopMarkets: true,
        order:
            type === MatchListType.RESULTS
                ? GQL.SportEventOrder.NONE
                : GQL.SportEventOrder.RANK_PERSONALIZED,
    };
}

export default useMatchListContainer;
